import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useCallback, useRef, useState } from "react";
import { toast } from 'sonner';
import { z } from "zod";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '~frontend/components/ui/alert-dialog';
import { Button } from "~frontend/components/ui/button";
import {
    Card, CardContent, CardHeader,
    CardTitle
} from '~frontend/components/ui/card';
import { Input } from "~frontend/components/ui/input";
import { useAppForm } from "~frontend/components/ui/tanstack-form";
import { createSessionName } from '~frontend/lib/utils';
import { trpc } from '~frontend/router';

export const Route = createFileRoute('/_LoggedIn/attendance/session/new')({
    component: RouteComponent,
})

const FormSchema = z.object({
    name: z.string().min(2, {
        message: "Name must be at least 2 characters.",
    }),
    duration: z.number().min(10, {
        message: "Duration must be at least 10 minutes.",
    }),
});


function RouteComponent() {
    const [open, setOpen] = useState(false)
    const hasConfirmed = useRef(false)
    const { data: session } = useQuery(trpc.session.getCurrentSession.queryOptions())
    const currentSession: typeof session | 'empty' = session ?? 'empty'
    const { mutate: createSession } = useMutation(trpc.session.createSession.mutationOptions())

    const form = useAppForm({
        validators: { onChange: FormSchema },
        defaultValues: {
            name: createSessionName(),
            duration: 90,
        },
        onSubmit: ({ value }) => {
            if (currentSession !== 'empty' && !hasConfirmed.current) {
                setOpen(true)
                return;
            }

            hasConfirmed.current = false


            createSession({
                name: value.name,
                durationInMinutes: value.duration,
            }, {
                onSuccess: () => {
                    toast.success('Session created successfully')
                },
                onError: () => {
                    toast.error('Failed to create session')
                }
            })
        }
    });

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
        },
        [form],
    );


    return (<>
        <Card className='bg-accent/40'>
            <CardHeader>
                {
                    currentSession === 'empty' ? (
                        <CardTitle>No Current Active Session Found</CardTitle>
                    ) : (
                        <CardTitle><span className='font-bold'>Current Active Session: </span><span className='font-semibold'>{currentSession.name}</span>
                        </CardTitle>
                    )
                }
            </CardHeader>
            {
                currentSession === 'empty' ? null : (
                    <CardContent>
                        <p><span className='font-bold'>Start Time: </span>{dayjs(currentSession.startTimestamp).format('DD-MMM-YYYY hh:mm A')}</p>
                        <p><span className='font-bold'>End Time: </span>{dayjs(currentSession.plannedEndTimestamp).format('DD-MMM-YYYY hh:mm A')}</p>
                        <p><span className='font-bold'>Duration: </span>{currentSession.plannedDurationInMinutes} minutes</p>
                    </CardContent>
                )
            }
        </Card>


        <Card className='px-8 bg-accent/20'>
            <CardHeader className='px-0'>
                <CardTitle className='text-2xl font-bold'>Create New Session</CardTitle>
            </CardHeader>

            <form.AppForm>
                <form className="space-y-6" onSubmit={handleSubmit}>
                    <form.AppField
                        name="name"
                        children={(field) => (
                            <field.FormItem>
                                <field.FormLabel>Name</field.FormLabel>
                                <field.FormControl>
                                    <Input
                                        placeholder="Session 2025-08-03"
                                        className='bg-accent/50'
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                    />
                                </field.FormControl>
                                <field.FormMessage />
                            </field.FormItem>
                        )}
                    />


                    <form.AppField
                        name="duration"
                        children={(field) => (
                            <field.FormItem>
                                <field.FormLabel>Duration</field.FormLabel>
                                <field.FormControl>
                                    <Input
                                        className='bg-accent/50'
                                        type="number"
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(Number(e.target.value))}
                                        onBlur={field.handleBlur}
                                    />
                                </field.FormControl>
                                <field.FormMessage />
                            </field.FormItem>
                        )}
                    />

                    <Button type="submit">Create Session</Button>
                </form>
            </form.AppForm>
        </Card>


        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>⚠️ End Current Session</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogDescription>
                    <p>Are you sure you want to end the current session?</p>
                    <p>This will end the current session and start a new one.</p>
                </AlertDialogDescription>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => {
                        setOpen(false)
                    }}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
                        hasConfirmed.current = true
                        setOpen(false)
                        form.handleSubmit()
                    }}>End Session & Create New</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>

    </>)
}